import React, { useEffect, useMemo } from 'react'
import { useSpring, useChain, animated, useSpringRef } from '@react-spring/web'
import type { SharedProps } from './types'
import {
	PRIMARY_GREEN,
	EOMS_BACKGROUND,
	EOMS_BORDER,
	EOMS_BORDER_RADIUS,
	EOMS_DARK_HIGHLIGHT,
} from './styles'
import { styled } from 'styled-components'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import { useLeaderboard } from '../../../context/provider'
import {
	LEADERBOARD_SCROLL_DURATION,
	LEADERBOARD_SCORE_FADE_IN_DURATION,
	LEADERBOARD_PLACEMENT_DURATION,
	LEADERBOARD_EXPANDING_DURATION,
} from './durations'
import {
	useLocalDispatch,
	startLocalSoundEffect,
	endLocalSoundEffect,
} from '../../../context'
import {
	LEADERBOARD_STATUS,
	SPECIAL_EFFECT_IDS as SOUNDS,
} from '@mission.io/mission-toolkit/constants'
import type {
	SimulationLeaderboard,
	SimulationLeaderboardElement,
} from '@mission.io/mission-toolkit'

const viewPort = 8 // how many elements to show at once
const overFill = 2 // how many elements to keep track of off screen
const padding = 1 // ph (player height unit)
const fontHeight = 2.5 // ph (player height unit)
const marginHeight = 1 // ph (player height unit)
const height = fontHeight + 2 * padding // ph (player height unit)
const totalHeight = height + marginHeight // ph (player height unit)

/**
 * Leaderboard - Displays the animated Leaderboard End Of Mission Sequence component. Sets up the props and
 *    guards against rerenders.
 *
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 *
 * @returns React$Node
 */
export default function Leaderboard({
	started,
	onFinish,
}: SharedProps): React$Node {
	const leaderboard = useLeaderboard().leaderboard

	useEffect(() => {
		if (
			started &&
			(!leaderboard ||
				(leaderboard.status ?? LEADERBOARD_STATUS.AVAILABLE) !==
					LEADERBOARD_STATUS.AVAILABLE)
		) {
			onFinish()
		}
	}, [started, onFinish, leaderboard])

	return useMemo(() => {
		if (
			leaderboard &&
			(leaderboard.status ?? LEADERBOARD_STATUS.AVAILABLE) ===
				LEADERBOARD_STATUS.AVAILABLE
		) {
			return (
				<AnimatedLeaderboard
					leaderboard={leaderboard}
					started={started}
					onFinish={onFinish}
				/>
			)
		}

		if (leaderboard?.status === LEADERBOARD_STATUS.UNAVAILABLE) {
			return <DisabledLeaderboard />
		}

		return <NoLeaderboard />
	}, [leaderboard, started, onFinish])
}

/**
 * AnimatedLeaderboard - animated leaderboard component for the End Of Mission Sequence which shows the leader board.
 *    and positions the mission score in the leaderboard if the mission exists in the leaderboard. The leaderboard may
 *   not contain the mission if analytics fail to generate or the school (such as the Mission.io school) is ignored
 *   during leaderboard calculations.
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 * @param  {SimulationLeaderboard} props.leaderboard - the leaderboard for the simulation
 *
 * @returns React$Node
 */
function AnimatedLeaderboard({
	leaderboard,
	started,
	onFinish,
}: {
	leaderboard: SimulationLeaderboard,
	started: boolean,
	onFinish: () => void,
}): React$Node {
	const dispatch = useLocalDispatch()

	const expandingProgressRef = useSpringRef()
	const { expandingProgress } = useSpring({
		ref: expandingProgressRef,
		config: { duration: LEADERBOARD_EXPANDING_DURATION },
		from: { expandingProgress: 0 },
		to: { expandingProgress: 1 },
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_APPEAR))
		},
	})

	const scrollSpringRef = useSpringRef()
	const location = leaderboard.location

	// Note the `1` comes from us having to insert a new element into the list
	const topIndex = -overFill + (location != null ? 1 : 0)

	// the index to consider the top of the leaderboard to position the end of the leaderboard in view
	const endOfLeaderboardTopIndex = Math.max(
		leaderboard.order.length - viewPort - overFill,
		topIndex
	)

	// the index to consider the top of the leaderboard to position the insertion index in the center of the view
	const centerInsertionLocationTopIndex = Math.max(
		Math.floor((location || 0) - viewPort / 2),
		topIndex
	)

	const { scrollLocation } = useSpring({
		ref: scrollSpringRef,
		config: { duration: LEADERBOARD_SCROLL_DURATION },
		from: {
			scrollLocation: endOfLeaderboardTopIndex,
		},
		to: {
			scrollLocation: centerInsertionLocationTopIndex,
		},
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUSPENSE_ELECTRIC))
		},
	})

	const currentScoreFadeInRef = useSpringRef()
	const { fadeInOpacity } = useSpring({
		ref: currentScoreFadeInRef,
		config: { duration: LEADERBOARD_SCORE_FADE_IN_DURATION },
		from: { fadeInOpacity: 0 },
		to: { fadeInOpacity: 1 },
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SPARKLE))
		},
	})

	const placementSpringRef = useSpringRef()
	const { placementProgress } = useSpring({
		ref: placementSpringRef,
		config: { duration: LEADERBOARD_PLACEMENT_DURATION },
		from: { placementProgress: 0 },
		to: { placementProgress: 1 },
		onRest: () => {
			dispatch(endLocalSoundEffect(SOUNDS.EOMS_SUSPENSE_ELECTRIC))
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SUPER_SPARKLE))
			onFinish()
		},
	})

	useChain(
		started
			? [
					expandingProgressRef,
					currentScoreFadeInRef,
					scrollSpringRef,
					placementSpringRef,
			  ]
			: []
	)

	return (
		<AnimatedScrollingLeaderboard
			scrollLocation={scrollLocation}
			placementProgress={placementProgress}
			fadeInOpacity={fadeInOpacity}
			expandingProgress={expandingProgress}
			items={leaderboard.order}
			insertionIndex={location}
			name={leaderboard.name}
		/>
	)
}

/**
 *
 *
 * @param {Object} props - the react props
 * @param  {Object} props.style - the "styles" animated from react spring
 * @param  {number} props.scrollLocation - the element index which should appear at the top of the leaderboard
 * @param  {number} props.fadeInOpacity - A number between 0 and 1 representing the progress of the fade in of the mission's entry before insertion
 * @param  {number} props.placementProgress - A number between 0 and 1 representing the progress of moving the mission's entry into the list
 *
 * @param  {SimulationLeaderboardElement[]} props.items - the elements in the leaderboard in order
 * @param  {?number} props.insertionIndex - the location (index) to insert the missions score into
 * @param  {string} props.name - the name of the leaderboard
 *
 * @returns React$Node
 */
const AnimatedScrollingLeaderboard = animated(function WindowedLeaderboard({
	placementProgress,
	scrollLocation: currentScroll,
	expandingProgress,
	fadeInOpacity,
	items,
	insertionIndex,
	name,
}: {
	scrollLocation: number,
	placementProgress: number,
	fadeInOpacity: number,
	expandingProgress: number,
	items: SimulationLeaderboardElement[],
	insertionIndex: ?number,
	name: string,
}): React$Node {
	let startIndex: number = Math.floor(currentScroll)

	const windowedItems = []
	let itemsToAdd = viewPort + 2 * overFill

	if (
		insertionIndex != null &&
		startIndex <= insertionIndex &&
		insertionIndex <= startIndex + itemsToAdd
	) {
		startIndex -= 1
		itemsToAdd += 1
	}

	for (let i = 0; i < itemsToAdd && startIndex + i < items.length; i++) {
		const index = startIndex + i
		if (index < 0) {
			windowedItems.push(<BlankItem key={index % itemsToAdd} />)
		} else if (index !== insertionIndex) {
			let ranking = index + 1

			if (
				placementProgress !== 1 &&
				insertionIndex != null &&
				index > insertionIndex
			) {
				ranking -= 1
			}
			windowedItems.push(
				<Item
					element={items[index]}
					key={index % itemsToAdd}
					ranking={ranking}
				/>
			)
		} else {
			windowedItems.push(
				<InsertionItem
					element={items[index]}
					key={index % itemsToAdd}
					ranking={index + 1}
					placementProgress={placementProgress}
				/>
			)
		}
	}

	return (
		<LeaderboardWrapper>
			<LeaderboardName>{name.toUpperCase()} LEADERBOARD</LeaderboardName>
			<LeaderboardBackground>
				<div
					style={{
						position: 'relative',
						height: cssPlayerHeightUnit(
							(viewPort + expandingProgress) * totalHeight
						),
					}}>
					<div
						style={{
							height: cssPlayerHeightUnit(
								(viewPort + placementProgress) * totalHeight
							),
							overflowY: 'hidden',
						}}>
						<animated.div
							style={{
								marginTop: cssPlayerHeightUnit(
									-totalHeight * ((((currentScroll % 1) + 1) % 1) + overFill)
								),
							}}>
							{windowedItems}
						</animated.div>
					</div>
					{insertionIndex != null ? (
						<MovingItem
							element={items[insertionIndex]}
							ranking={insertionIndex + 1}
							placementProgress={placementProgress}
							targetY={
								((insertionIndex - startIndex - overFill) % viewPort) *
								totalHeight
							}
							opacity={fadeInOpacity}
						/>
					) : null}
				</div>
			</LeaderboardBackground>
		</LeaderboardWrapper>
	)
})
/**
 * Item - the standard element to display an item in the leaderboard
 *
 * @param  {SimulationLeaderboardElement} element - the element in the leaderboard to display
 * @param  {number} ranking - the ranking of the element in the leaderboard
 *
 * @returns React$Node
 */
function Item({
	element,
	ranking,
}: {
	element: SimulationLeaderboardElement,
	ranking: number,
}): React$Node {
	return (
		<StyledItem>
			<Element element={element} ranking={ranking} />
		</StyledItem>
	)
}

/**
 * InsertionItem - A placeholder in the list for the location of the current mission's entry.
 *
 * @param  {SimulationLeaderboardElement} element - the element in the leaderboard to display
 * @param  {number} ranking - the ranking of the element in the leaderboard
 * @param  {number} placementProgress - a number between 0 and 1 denting the progress of moving current mission's item into the leaderboard
 *
 * @returns React$Node
 */
function InsertionItem({
	element,
	ranking,
	placementProgress,
}: {
	element: SimulationLeaderboardElement,
	ranking: number,
	placementProgress: number,
}): React$Node {
	return (
		<StyledItem
			style={{
				opacity: 0,
				maxHeight: cssPlayerHeightUnit(height * placementProgress),
				padding: cssPlayerHeightUnit(padding * placementProgress),
				margin: `${cssPlayerHeightUnit(
					marginHeight * (1.5 * placementProgress - 0.5)
				)} 0`,
			}}>
			<Element element={element} ranking={ranking} />
		</StyledItem>
	)
}

/**
 * MovingItem - the current mission's entry which appears and gets moved into the list
 *
 * @param  {SimulationLeaderboardElement} element - the element in the leaderboard to display
 * @param  {number} ranking - the ranking of the element in the leaderboard
 * @param  {number} placementProgress - a number between 0 and 1 denting the progress of moving this item into the leaderboard
 * @param  {number} targetY - a number denoting the location on the screen to move the leaderboard item to
 * @param  {number} opacity - the opacity of this item
 *
 * @returns React$Node
 */
function MovingItem({
	element,
	ranking,
	placementProgress,
	targetY,
	opacity,
}: {
	element: SimulationLeaderboardElement,
	ranking: number,
	placementProgress: number,
	targetY: number,
	opacity: number,
}): React$Node {
	return (
		<StyledItem
			style={{
				position: 'absolute',
				left: 0,
				top: cssPlayerHeightUnit(
					viewPort * totalHeight * (1 - placementProgress) +
						targetY * placementProgress
				),
				backgroundColor: PRIMARY_GREEN,
				opacity,
			}}>
			<Element
				element={element}
				ranking={ranking}
				rankingOpacity={placementProgress}
			/>
		</StyledItem>
	)
}

/**
 * Element - displays the element of the list
 *
 * @param  {SimulationLeaderboardElement} element - the element to display
 * @param  {number} ranking - the ranking of the element
 * @param  {number = 1} rankingOpacity - the opacity of the element
 *
 * @returns React$Node
 */
function Element({
	element,
	ranking,
	rankingOpacity = 1,
}: {
	element: SimulationLeaderboardElement,
	ranking: number,
	rankingOpacity?: number,
}): React$Node {
	const personalizedIdentifiers = []
	if (element.teacherName) {
		personalizedIdentifiers.push(element.teacherName)
	}
	if (element.className) {
		if (personalizedIdentifiers.length) {
			personalizedIdentifiers.push(' - ')
		}
		personalizedIdentifiers.push(<i>{element.className}</i>)
	}
	return (
		<StyledElement>
			<div>
				<span style={{ opacity: rankingOpacity }}>{ranking}. </span>
				{element.schoolName}
				{element.state ? `, ${element.state}` : ''}
				{personalizedIdentifiers.length ? (
					<> ({personalizedIdentifiers})</>
				) : null}
			</div>
			<div>{Math.floor(element.score).toLocaleString()}</div>
		</StyledElement>
	)
}

/**
 * NoLeaderboard - A component to display if a leaderboard is not available for this missions's simulation
 *
 * @returns React$Node
 */
function NoLeaderboard(): React$Node {
	return (
		<LeaderboardWrapper>
			<LeaderboardName>NO LEADERBOARD AVAILABLE</LeaderboardName>
		</LeaderboardWrapper>
	)
}

/**
 * DisabledLeaderboard - A component to display if a leaderboard is not disabled for the simulation
 *
 * @returns React$Node
 */
function DisabledLeaderboard(): React$Node {
	return (
		<LeaderboardWrapper>
			<LeaderboardName>
				Leaderboard is disabled for this mission
			</LeaderboardName>
		</LeaderboardWrapper>
	)
}

const StyledItem = styled.div`
	background-color: ${EOMS_DARK_HIGHLIGHT};
	position: relative;
	height: ${cssPlayerHeightUnit(height)};
	max-height: ${cssPlayerHeightUnit(height)};
	padding: ${cssPlayerHeightUnit(padding)} 2.5%;
	width: 95%;
	left: 0;
	top: 0;
	margin: ${cssPlayerHeightUnit(marginHeight)} 2.5%;
`

const BlankItem = styled(StyledItem)`
	background-color: transparent;
	color: transparent;
	border-color: transparent;
`

const StyledElement = styled.div`
	font-size: ${cssPlayerHeightUnit(fontHeight)};
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
`

const LeaderboardWrapper = styled.div`
	width: 80%;
`

const LeaderboardName = styled.div`
	background-color: ${EOMS_BACKGROUND};
	font-size: ${cssPlayerHeightUnit(2.75)};
	padding: ${cssPlayerHeightUnit(3.75)};
	text-align: center;
	border: ${EOMS_BORDER};
`

const LeaderboardBackground = styled.div`
	background-color: ${EOMS_BACKGROUND};
	border: ${EOMS_BORDER};
	border-top: none;
	border-radius: 0 0 ${EOMS_BORDER_RADIUS} ${EOMS_BORDER_RADIUS};
	padding: ${cssPlayerHeightUnit(1)} 0;
`
